/**
 * Shared item form constants used by both web and catalog apps.
 *
 * These constants control conditional form logic (which categories show
 * equipment fields, land fields, etc.) and shared type definitions.
 * Both platforms import from here to stay in sync.
 */

// ---------------------------------------------------------------------------
// Category kind helpers
// ---------------------------------------------------------------------------
//
// Form logic must branch on category.kind flags, never on literal IDs.
// Tenants may customize their category set; hardcoded ID checks break those
// tenants. See packages/core-admin/src/categories/index.ts for the flag defs.

import { DEFAULT_CATEGORIES, type Category } from '../categories';

/**
 * Resolves a category by id against the supplied list, or `DEFAULT_CATEGORIES`
 * if none is supplied. The default list is the static, compile-time category
 * set that ships with the app; callers operating on a tenant-customized
 * category list MUST pass that list explicitly — otherwise tenant-specific
 * categories (e.g. a custom "Boats" category) will be invisible and the
 * helper will return `false` for them.
 *
 * Tenant-config-backed categories are a planned follow-up; for now most
 * callers can rely on the default because DEFAULT_CATEGORIES is the only
 * source of truth in production.
 */
function lookupCategory(
  categoryId: string | null | undefined,
  categories: readonly Category[] = DEFAULT_CATEGORIES,
): Category | undefined {
  if (!categoryId) return undefined;
  return categories.find((c) => c.id === categoryId);
}

/** True when the category's items normally have a title (vehicles, trailers, etc.). Drives the "Titled Item" checkbox. */
export function isTitledCategory(
  categoryId: string | null | undefined,
  categories?: readonly Category[],
): boolean {
  return !!lookupCategory(categoryId, categories)?.is_titled;
}

/** True when items in this category use a 17-character NHTSA-format VIN. Drives the VIN-vs-Serial input and the VIN auto-lookup affordance. */
export function usesVin(
  categoryId: string | null | undefined,
  categories?: readonly Category[],
): boolean {
  return !!lookupCategory(categoryId, categories)?.uses_vin;
}

/** True for real estate / land categories — drives acreage + per-acre pricing UI. */
export function isLandCategory(
  categoryId: string | null | undefined,
  categories?: readonly Category[],
): boolean {
  return !!lookupCategory(categoryId, categories)?.is_land;
}

/** True for equipment-shaped categories — drives make/model/year/serial fields. */
export function isEquipmentCategory(
  categoryId: string | null | undefined,
  categories?: readonly Category[],
): boolean {
  return !!lookupCategory(categoryId, categories)?.equipment_form;
}

/** True when the category uses mileage (otherwise hours, when equipment_form is on). */
export function usesMileage(
  categoryId: string | null | undefined,
  categories?: readonly Category[],
): boolean {
  return lookupCategory(categoryId, categories)?.uses_mileage_or_hours === 'mileage';
}

/** Label for the mileage/hours field on an equipment category. */
export function mileageOrHoursLabel(
  categoryId: string | null | undefined,
  categories?: readonly Category[],
): 'Mileage' | 'Hours' {
  return usesMileage(categoryId, categories) ? 'Mileage' : 'Hours';
}

// ---------------------------------------------------------------------------
// Deprecated string-ID constants
// ---------------------------------------------------------------------------
// Kept exported so external consumers don't break mid-rollout. New code MUST
// use the helpers above. These will be removed once all in-repo callers have
// been migrated (tracked alongside slices 2-5 of the enum refactor).

/** @deprecated Use {@link isTitledCategory} or {@link usesMileage} instead. */
export const TRANSPORT_CATEGORY_ID = 'cat-transport';

/** @deprecated Use {@link isLandCategory} instead. */
export const LAND_CATEGORY_ID = 'cat-land';

/** @deprecated Use {@link isEquipmentCategory} instead. */
export const EQUIPMENT_CATEGORY_IDS = [
  'cat-construction',
  'cat-farm',
  'cat-transport',
] as const;

// ---------------------------------------------------------------------------
// Title statuses
// ---------------------------------------------------------------------------

/**
 * All possible title statuses for inventory items.
 * Web uses the full set; mobile may display a subset but stores the same values.
 *
 * 'has_title' is a deprecated alias for 'seller_has_title' — kept for backward
 * compatibility with items already stored in the catalog app's local storage.
 * New code should use 'seller_has_title'.
 */
export const TITLE_STATUSES = [
  'seller_has_title',
  'bank_has_title',
  'hold_check',
  'no_title',
  'applied_for',
  'needs_signature',
  'on_file',
] as const;

export type TitleStatus = (typeof TITLE_STATUSES)[number] | 'has_title';

/** Human-readable labels for each title status (includes deprecated alias) */
export const TITLE_STATUS_LABELS: Record<TitleStatus, string> = {
  seller_has_title: 'Seller has title',
  has_title: 'Seller has title',
  bank_has_title: 'Bank has title',
  hold_check: 'Hold check',
  no_title: 'No title',
  applied_for: 'Applied for',
  needs_signature: 'Needs signature',
  on_file: 'On file ✓',
};

/**
 * BL-2361: the Title Status cell words the lot-list CSV export writes. Frozen at
 * their pre-sentence-case bytes so a spreadsheet keyed on them keeps working;
 * every screen reads {@link TITLE_STATUS_LABELS}.
 */
export const TITLE_STATUS_EXPORT_LABELS: Record<TitleStatus, string> = {
  seller_has_title: 'Seller has Title',
  has_title: 'Seller has Title',
  bank_has_title: 'Bank Has Title',
  hold_check: 'Hold Check',
  no_title: 'No Title',
  applied_for: 'Applied For',
  needs_signature: 'Needs Signature',
  on_file: 'On File ✓',
};

/**
 * BL-2319: the label a lot list shows for a lot's title status — the lot list
 * cell and its CSV export both read it. A status only means something on a
 * Titled Item, so a lot that isn't one shows nothing whatever it carries.
 *
 * BL-2361: the export names itself so it writes its frozen words
 * ({@link TITLE_STATUS_EXPORT_LABELS}); every screen gets the sentence-case ones.
 *
 * @param lot - The lot's `isTitledItem` flag and stored `titleStatus`.
 * @param surface - Who is asking: a screen (default) or the CSV export.
 * @returns The status's label, or `null` when the column shows "—".
 */
export function titleStatusDisplayLabel(
  lot: {
    isTitledItem: boolean | null | undefined;
    titleStatus: TitleStatus | null | undefined;
  },
  surface: 'screen' | 'export' = 'screen',
): string | null {
  if (lot.isTitledItem !== true || lot.titleStatus == null) return null;
  return (surface === 'export' ? TITLE_STATUS_EXPORT_LABELS : TITLE_STATUS_LABELS)[lot.titleStatus];
}

/** BL-2319: the lot-list Title Status filter's "Not titled" bucket — lots that aren't Titled Items. */
export const TITLE_STATUS_FILTER_NOT_TITLED = 'not_titled';

/** BL-2319: the filter's "Titled, no status" bucket — Titled Items with no title status yet. */
export const TITLE_STATUS_FILTER_NO_STATUS = 'titled_no_status';

/**
 * BL-2319: every value the lot-list `titleStatus` filter accepts, in the order
 * the facet lists them. One source for the server parser and the client facet.
 */
export const TITLE_STATUS_FILTER_VALUES = [
  TITLE_STATUS_FILTER_NOT_TITLED,
  TITLE_STATUS_FILTER_NO_STATUS,
  ...TITLE_STATUSES,
] as const;

/** A value the lot-list `titleStatus` filter accepts. */
export type TitleStatusFilterValue = (typeof TITLE_STATUS_FILTER_VALUES)[number];

const TITLE_STATUS_FILTER_VALUE_SET: ReadonlySet<string> = new Set(TITLE_STATUS_FILTER_VALUES);

/**
 * Narrows a raw `titleStatus` filter value off the URL to one the filter knows.
 *
 * @param value - One comma-split value from the query string.
 * @returns `true` when it is a known filter value.
 */
export function isTitleStatusFilterValue(value: string): value is TitleStatusFilterValue {
  return TITLE_STATUS_FILTER_VALUE_SET.has(value);
}

/**
 * Normalizes a stored title-status value to one the `inventory_title_status`
 * DB enum accepts. The only legacy alias is `has_title` — the old name for
 * `seller_has_title`, still present in some offline catalog-app rows the DB
 * enum rejects. Valid statuses, `null`/`undefined`, and unknown strings pass
 * through unchanged (callers validate separately).
 *
 * @param value raw title-status string from a client payload or legacy row
 * @returns `'seller_has_title'` for the `has_title` alias, otherwise `value` unchanged
 */
export function normalizeTitleStatus(
  value: string | null | undefined,
): string | null | undefined {
  return value === 'has_title' ? 'seller_has_title' : value;
}

// BL-1088: `normalizeInventoryStatus` was deleted here. It mapped the catalog
// app's `catalogued` to the `inventory_item_status` enum's `ready` — but BL-311
// dropped both the `inventory_items.status` column AND that enum, so there was
// nothing left to normalize toward. Its last two consumers had already gone
// (the sync service at BUG-896, the catalog client at BL-891), leaving it dead.

// ---------------------------------------------------------------------------
// Mileage units (transport category)
// ---------------------------------------------------------------------------

/** Unit for transport mileage — non-transport equipment always uses hours */
export type MileageUnit = 'miles' | 'km';

/**
 * Options for the miles/km selector in forms. The labels are the short units
 * (BL-2547): the dropdown sits beside the number in the phone sheet's Equipment
 * row, where "Kilometers" did not fit. The values are unchanged.
 */
export const MILEAGE_UNIT_OPTIONS: { value: MileageUnit; label: string }[] = [
  { value: 'miles', label: 'mi' },
  { value: 'km', label: 'km' },
];

/** Short suffix for display (e.g., "45,000 mi") */
export const MILEAGE_UNIT_SUFFIX: Record<MileageUnit, string> = {
  miles: 'mi',
  km: 'km',
};

// ---------------------------------------------------------------------------
// Title mailing + sync status (BL-902)
// ---------------------------------------------------------------------------
//
// Relocated here from apps/admin-web/src/types/inventory.ts so the shared
// `mapInventoryRow` mapper (packages/core-admin) can type its output honestly —
// a package can't import an app type. Re-exported from `@/types/inventory`, so
// every existing importer is unchanged (same precedent as TitleStatus above).

/**
 * Title mailing status (for sending title to buyer after sale).
 *
 * BUG-1091: promoted from a hand-written union to a runtime `as const` array so
 * the write path can validate against it (`InventoryTitleMailingStatusSchema`)
 * — same shape as `TITLE_STATUSES` above. The values ARE the
 * `inventory_title_mailing_status` pgEnum members; keep the two in step.
 */
export const TITLE_MAILING_STATUSES = [
    'pending',           // Title ready to mail but not yet sent
    'sent',              // Title mailed, no tracking
    'sent_tracking',     // Title mailed with tracking
    'awaiting_receipt',  // Sent via certified mail, waiting for green card
    'picked_up',         // Picked up in person
    'complete',          // Certified receipt received and on file
] as const;

export type TitleMailingStatus = (typeof TITLE_MAILING_STATUSES)[number];

/**
 * BL-2333 (ADR 044 — Must Consolidate): the ONE mailing label set. The drawer's
 * dropdown (`TITLE_MAILING_STATUS_OPTIONS`), the lot lists' column, filter and
 * sort, and the CSV export all read it. Sentence case (Dan Saper, 2026-10-08).
 */
export const TITLE_MAILING_STATUS_LABELS: Record<TitleMailingStatus, string> = {
  pending: 'Ready to mail',
  sent: 'Sent (no tracking)',
  sent_tracking: 'Sent (with tracking)',
  awaiting_receipt: 'Awaiting receipt',
  picked_up: 'Picked up in person',
  complete: 'Complete ✓',
};

/**
 * BL-2333: the label a lot list shows for a lot's title mailing status — the
 * list cell and its CSV export both read it. Like the title status, it only
 * means something on a Titled Item.
 *
 * @param lot - The lot's `isTitledItem` flag and stored `titleMailingStatus`.
 * @returns The status's label, or `null` when the column shows "—".
 */
export function titleMailingStatusDisplayLabel(lot: {
  isTitledItem: boolean | null | undefined;
  titleMailingStatus: TitleMailingStatus | null | undefined;
}): string | null {
  if (lot.isTitledItem !== true || lot.titleMailingStatus == null) return null;
  return TITLE_MAILING_STATUS_LABELS[lot.titleMailingStatus];
}

/**
 * BL-2248: the auction Lots tab's Invoice filter — its one value, "Not invoiced":
 * a sold lot no invoice lists, sent or Preparing. One source for the server
 * parser, the client facet and the auction header's link.
 */
export const INVOICE_FILTER_NOT_INVOICED = 'not_invoiced';

/** BL-2248: the Invoice filter's option label. */
export const INVOICE_FILTER_NOT_INVOICED_LABEL = 'Not invoiced';

/**
 * BL-2922: the Invoice filter's inverse value, "Invoiced": a sold lot an invoice
 * lists, sent or Preparing. Both values together is every sold lot.
 */
export const INVOICE_FILTER_INVOICED = 'invoiced';

/** BL-2922: the Invoiced option's label. */
export const INVOICE_FILTER_INVOICED_LABEL = 'Invoiced';

/** BL-2333: the lot-list Title Mailing Status filter's "Not set" bucket — Titled Items with no mailing status. */
export const TITLE_MAILING_STATUS_FILTER_NOT_SET = 'not_set';

/**
 * BL-2333: every value the lot-list `titleMailingStatus` filter accepts, in the
 * order the facet lists them. One source for the server parser and the client facet.
 */
export const TITLE_MAILING_STATUS_FILTER_VALUES = [
  TITLE_MAILING_STATUS_FILTER_NOT_SET,
  ...TITLE_MAILING_STATUSES,
] as const;

/** A value the lot-list `titleMailingStatus` filter accepts. */
export type TitleMailingStatusFilterValue = (typeof TITLE_MAILING_STATUS_FILTER_VALUES)[number];

const TITLE_MAILING_STATUS_FILTER_VALUE_SET: ReadonlySet<string> = new Set(TITLE_MAILING_STATUS_FILTER_VALUES);

/**
 * Narrows a raw `titleMailingStatus` filter value off the URL to one the filter knows.
 *
 * @param value - One comma-split value from the query string.
 * @returns `true` when it is a known filter value.
 */
export function isTitleMailingStatusFilterValue(value: string): value is TitleMailingStatusFilterValue {
  return TITLE_MAILING_STATUS_FILTER_VALUE_SET.has(value);
}

/** Offline-friendly sync state for catalog-app-created rows. */
export type SyncStatus = 'synced' | 'pending' | 'conflict';

// ---------------------------------------------------------------------------
// Land pricing
// ---------------------------------------------------------------------------

/**
 * How a land lot's price is denominated.
 *
 * Alias of the canonical {@link LotPricingType} in `@auctogon/core/isomorphic/land`
 * (BL-1525) — the union is declared exactly once, beside the shared land
 * predicate that reads it, so admin and bidder cannot drift on the spelling.
 * Kept as a named alias because every admin call site already imports this name.
 */
export type { LotPricingType as LandPricingType } from '@auctogon/core/isomorphic/land';

// ---------------------------------------------------------------------------
// Title state
// ---------------------------------------------------------------------------

// BL-1449: the Title State option list + its derived code set, moved here from
// admin-web so the server-side write rule and the client dropdown read the same
// data. See ./us-states.ts for why.
export { US_STATES, US_STATE_CODES } from './us-states';
